class ProductBundleOffer extends HTMLElement {
  constructor() {
    super();
    this.selectedOffer = this.dataset.defaultOffer || 'one';
    this.mainVariants = [];
    this.secondaryOptions = [];
    this.variantPriceMap = {};
    this.currentVariant = null;
    this.offerSelections = { one: [], two: [] };
    this.countdownTimer = null;
    this.offerCountdownTimers = [];
    this.priceObserver = null;
    this.priceSyncTimer = null;
    this.priceSetupAttempts = 0;
    this.displayVariantRate = 1;
    this.displayShopRate = 1;
    this.onDocumentChange = this.onDocumentChange.bind(this);
  }

  connectedCallback() {
    this.optionButtons = Array.from(this.querySelectorAll('[data-offer]'));
    this.mainVariants = this.readJson('[data-main-variants]') || [];
    this.secondaryOptions = this.readJson('[data-secondary-options]') || [];
    this.variantPriceMap = this.readJson('[data-variant-price-map]') || {};
    this.currentVariant = this.readJson('[data-current-variant]') || null;

    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectOffer(button.dataset.offer));
    });

    document.addEventListener('change', this.onDocumentChange, true);

    this.initializeSelections();
    this.selectOffer(this.selectedOffer, false);
    this.renderAll();
    this.setupThemePriceSync();
    this.startCountdown();
    this.startOfferCountdowns();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange, true);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.offerCountdownTimers.forEach((timer) => clearInterval(timer));
    this.offerCountdownTimers = [];
    if (this.priceSyncTimer) clearTimeout(this.priceSyncTimer);
    this.priceObserver?.disconnect();
  }

  readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null');
    } catch {
      return null;
    }
  }

  getProductForm() {
    const section = this.closest('.shopify-section');
    const forms = Array.from(section?.querySelectorAll('product-form-component') || []);
    return forms.find((form) => form.dataset.productId === this.dataset.productId) || forms[0] || null;
  }

  getCurrentVariantId() {
    return this.getProductForm()?.querySelector('input[name="id"]')?.value || '';
  }

  getCurrentMainVariant() {
    const id = this.getCurrentVariantId();
    return this.mainVariants.find((variant) => String(variant.variantId) === String(id)) || this.mainVariants[0] || null;
  }

  getOptionsPool() {
    const availableSecondary = this.secondaryOptions.filter((option) => option.available);
    const availableMain = this.mainVariants.filter((option) => option.available);
    return availableSecondary.length ? availableSecondary : availableMain;
  }

  initializeSelections() {
    const current = this.getCurrentMainVariant();
    const pool = this.getOptionsPool();
    const currentPoolIndex = pool.findIndex(
      (option) => String(option.variantId) === String(current?.variantId)
    );

    for (const offer of ['one', 'two']) {
      const total = this.getTotalSlots(offer);
      this.offerSelections[offer] = Array.from({ length: total }, (_, index) => {
        if (!pool.length) return current || null;

        if (currentPoolIndex >= 0) {
          return pool[(currentPoolIndex + index) % pool.length] || current || null;
        }

        if (index === 0 && current) return current;

        return pool[(index - 1) % pool.length] || current || null;
      });
    }
  }

  onDocumentChange(event) {
    if (event.target?.closest?.('[name="doubly-currencies"]')) {
      // The theme price is the source of truth. Its observer normally fires
      // first; this delayed pass is only a fallback for converter implementations
      // that update the price without replacing its DOM nodes.
      this.scheduleThemePriceSync(250);
      return;
    }

    const section = this.closest('.shopify-section');
    if (!section?.contains(event.target)) return;

    const picker = event.target.closest('variant-picker, swatches-variant-picker-component');
    if (!picker) return;

    const changedControl = event.target;
    const immediateVariantId =
      changedControl?.dataset?.variantId ||
      changedControl?.selectedOptions?.[0]?.dataset?.variantId ||
      picker.querySelector('input:checked[data-variant-id]')?.dataset?.variantId ||
      picker.querySelector('select option:checked[data-variant-id]')?.dataset?.variantId;

    const immediateVariant = this.mainVariants.find(
      (variant) => String(variant.variantId) === String(immediateVariantId)
    );

    if (immediateVariant) {
      this.applyMainVariantImmediately(immediateVariant);
      return;
    }

    queueMicrotask(() => {
      const current = this.getCurrentMainVariant();
      if (current) this.applyMainVariantImmediately(current);
    });
  }

  applyMainVariantImmediately(variant) {
    this.dataset.currentCompareAtPrice = String(variant?.compareAtPrice || 0);

    for (const offer of ['one', 'two']) {
      if (this.offerSelections[offer].length) {
        this.offerSelections[offer][0] = variant;
        this.renderOffer(offer);
        this.updateOfferPrice(offer);
      }
    }
  }

  getPaidQuantity(offer) {
    return Number(this.dataset[`offer${offer === 'one' ? 'One' : 'Two'}Paid`] || 1);
  }

  getFreeQuantity(offer) {
    return Number(this.dataset[`offer${offer === 'one' ? 'One' : 'Two'}Free`] || 0);
  }

  getTotalSlots(offer) {
    return this.getPaidQuantity(offer) + this.getFreeQuantity(offer);
  }

  getOfferType(offer) {
    return this.dataset[`offer${offer === 'one' ? 'One' : 'Two'}Type`] || 'free_items';
  }

  selectOffer(offer, announce = true) {
    this.selectedOffer = offer === 'two' ? 'two' : 'one';

    this.optionButtons.forEach((button) => {
      const selected = button.dataset.offer === this.selectedOffer;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    this.querySelector('[data-offer-one-details]').hidden = this.selectedOffer !== 'one';
    this.querySelector('[data-offer-two-details]').hidden = this.selectedOffer !== 'two';

    if (announce) {
      this.dispatchEvent(new CustomEvent('bundle-offer:change', { bubbles: true }));
    }
  }

  renderAll() {
    this.renderOffer('one');
    this.renderOffer('two');
    this.updateOfferPrice('one');
    this.updateOfferPrice('two');
  }

  renderOffer(offer) {
    const container = this.querySelector(`[data-offer-${offer}-slots]`);
    if (!container) return;

    container.innerHTML = '';
    const pool = this.getOptionsPool();

    this.offerSelections[offer].forEach((storedSelection, index) => {
      const fallback =
        storedSelection ||
        pool[index % Math.max(pool.length, 1)] ||
        pool[0] ||
        this.getCurrentMainVariant() ||
        null;

      if (fallback && !storedSelection) {
        this.offerSelections[offer][index] = fallback;
      }

      const selection = this.offerSelections[offer][index];

      const slot = document.createElement('div');
      slot.className = 'bundle-offer__slot';

      const slotIndex = document.createElement('span');
      slotIndex.className = 'bundle-offer__slot-index';
      slotIndex.textContent = String(index + 1);
      slotIndex.setAttribute('aria-hidden', 'true');

      const image = document.createElement('span');
      image.className = 'bundle-offer__slot-image';

      if (selection?.image) {
        image.innerHTML = `<img src="${this.escapeHtml(selection.image)}" alt="" loading="eager">`;
      }

      const control = document.createElement('div');
      control.className = 'bundle-offer__slot-control';

      const select = document.createElement('select');
      select.className = 'bundle-offer__slot-select';
      select.setAttribute('aria-label', 'Choose variant');

      for (const option of pool) {
        const optionElement = document.createElement('option');
        optionElement.value = String(option.variantId);
        optionElement.disabled = !option.available;

        const hasVariant =
          option.variantTitle &&
          option.variantTitle !== 'Default Title';

        optionElement.textContent = hasVariant
          ? option.variantTitle
          : option.productTitle;

        optionElement.selected =
          String(selection?.variantId) === String(option.variantId);

        select.appendChild(optionElement);
      }

      select.addEventListener('change', () => {
        const selected = pool.find(
          (option) => String(option.variantId) === select.value
        );

        if (!selected) return;

        this.offerSelections[offer][index] = selected;
        this.updateSlotImage(slot, selected);
        this.updateOfferPrice(offer);

        if (
          index === 0 &&
          String(selected.productId) === String(this.dataset.productId)
        ) {
          this.syncMainPickerImmediately(selected);
        }
      });

      control.appendChild(select);
      slot.append(slotIndex, image, control);
      container.appendChild(slot);
    });
  }

  syncMainPickerImmediately(option) {
    const section = this.closest('.shopify-section');
    const picker = Array.from(section?.querySelectorAll('variant-picker, swatches-variant-picker-component') || [])
      .find((item) => item.dataset.productId === this.dataset.productId);

    if (!picker || !Array.isArray(option.options)) return;

    const productForm = this.getProductForm();
    const hiddenVariantInput = productForm?.querySelector('input[name="id"]');

    if (hiddenVariantInput) {
      hiddenVariantInput.value = String(option.variantId);
      hiddenVariantInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    let trigger = null;
    const selects = Array.from(picker.querySelectorAll('select[name^="options["]'));

    option.options.forEach((value, index) => {
      const radios = Array.from(
        picker.querySelectorAll(`input[type="radio"][data-fieldset-index="${index}"]`)
      );
      const radio = radios.find((input) => input.value === value);

      if (radio) {
        radio.checked = true;
        radio.dataset.currentChecked = 'true';
        trigger = radio;
      }

      const select = selects[index];
      if (select) {
        select.value = value;
        trigger = select;
      }
    });

    this.applyMainVariantImmediately(option);

    if (trigger) {
      trigger.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateSlotImage(slot, selection) {
    const image = slot.querySelector('.bundle-offer__slot-image');
    if (!image) return;

    image.innerHTML = selection?.image
      ? `<img src="${this.escapeHtml(selection.image)}" alt="" loading="eager">`
      : '';
  }

  updateOfferPrice(offer) {
    const selections = this.offerSelections[offer].filter(Boolean);
    const displayCurrency = this.getDisplayCurrency();
    const amounts = this.calculateOfferAmounts(
      offer,
      selections,
      this.displayVariantRate,
      this.displayShopRate
    );

    this.renderOfferPriceValues(
      offer,
      amounts.total,
      amounts.finalPrice,
      amounts.compareTotal,
      displayCurrency
    );
  }

  calculateOfferAmounts(offer, selections, variantRate = 1, storeToOutputRate = 1) {
    const prices = selections.map((item) =>
      Math.max(0, Math.round(Number(item.price || 0) * variantRate))
    );
    const total = prices.reduce((sum, price) => sum + price, 0);
    const compareTotal = selections.reduce((sum, item, index) => {
      const backendComparePrice = Math.max(
        0,
        Math.round(this.getSelectionCompareAtPrice(item) * variantRate)
      );
      return sum + Math.max(prices[index] || 0, backendComparePrice);
    }, 0);
    const type = this.getOfferType(offer);
    const freeQty = this.getFreeQuantity(offer);
    let finalPrice = total;

    if (type === 'free_items' && freeQty > 0 && prices.length) {
      const freeValue = [...prices]
        .sort((a, b) => a - b)
        .slice(0, freeQty)
        .reduce((sum, price) => sum + price, 0);
      finalPrice = Math.max(0, total - freeValue);
    } else if (type === 'percentage_off') {
      const percent = this.readOfferNumber(offer, 'Percent');
      finalPrice = Math.max(0, Math.round(total * (1 - percent / 100)));
    } else if (type === 'fixed_discount') {
      const discount = Math.round(
        this.readOfferNumber(offer, 'Fixed') * 100 * storeToOutputRate
      );
      finalPrice = Math.max(0, total - discount);
    } else if (type === 'custom_price') {
      finalPrice = Math.max(
        0,
        Math.round(this.readOfferNumber(offer, 'CustomPrice') * 100 * storeToOutputRate)
      );
    }

    const hasOfferDiscount = finalPrice < total;
    const originalPrice = hasOfferDiscount
      ? total
      : Math.max(total, compareTotal);

    return { total, finalPrice, compareTotal: originalPrice };
  }

  getSelectionCompareAtPrice(selection) {
    const mappedComparePrice = Number(
      this.variantPriceMap?.[String(selection?.variantId)]?.compareAtPrice || 0
    );
    if (mappedComparePrice > 0) return mappedComparePrice;

    const directComparePrice = Number(selection?.compareAtPrice || 0);
    if (directComparePrice > 0) return directComparePrice;

    const matchingMainVariant = this.mainVariants.find(
      (variant) => String(variant.variantId) === String(selection?.variantId)
    );
    const fallbackComparePrice = Number(matchingMainVariant?.compareAtPrice || 0);
    if (fallbackComparePrice > 0) return fallbackComparePrice;

    const matchingSecondaryVariant = this.secondaryOptions.find(
      (variant) => String(variant.variantId) === String(selection?.variantId)
    );
    const secondaryComparePrice = Number(matchingSecondaryVariant?.compareAtPrice || 0);
    if (secondaryComparePrice > 0) return secondaryComparePrice;

    const currentVariantMatches =
      String(this.currentVariant?.id) === String(selection?.variantId);
    const currentVariantComparePrice = Number(this.currentVariant?.compare_at_price || 0);
    if (currentVariantMatches && currentVariantComparePrice > 0) {
      return currentVariantComparePrice;
    }

    const sameProductVariant = [...this.mainVariants, ...this.secondaryOptions].find(
      (variant) =>
        String(variant.productId) === String(selection?.productId) &&
        Number(variant.compareAtPrice || 0) > Number(variant.price || 0)
    );
    const sameProductComparePrice = Number(sameProductVariant?.compareAtPrice || 0);
    if (sameProductComparePrice > 0) return sameProductComparePrice;

    const isCurrentProduct =
      !selection?.productId ||
      String(selection.productId) === String(this.dataset.productId);
    const currentComparePrice = Number(this.dataset.currentCompareAtPrice || 0);
    return isCurrentProduct && currentComparePrice > 0 ? currentComparePrice : 0;
  }

  readOfferNumber(offer, suffix) {
    const prefix = offer === 'one' ? 'offerOne' : 'offerTwo';
    const value = Number(this.dataset[`${prefix}${suffix}`] || 0);
    return Number.isFinite(value) ? value : 0;
  }

  renderOfferPriceValues(
    offer,
    total,
    finalPrice,
    compareTotal,
    currency
  ) {
    const priceNode = this.querySelector(`[data-offer-${offer}-current]`);
    const compareNode = this.querySelector(`[data-offer-${offer}-original]`);
    const savingNode = this.querySelector(`[data-offer-${offer}-benefit]`);
    const originalPrice = Math.max(total, compareTotal || 0);

    if (priceNode) {
      priceNode.removeAttribute('aria-busy');
      this.renderMoneyNode(priceNode, finalPrice, currency);
    }

    if (compareNode) {
      compareNode.removeAttribute('aria-busy');
      if (originalPrice > finalPrice) {
        this.renderMoneyNode(compareNode, originalPrice, currency);
      } else {
        this.clearMoneyNode(compareNode);
      }
      compareNode.hidden = originalPrice <= finalPrice;
    }

    if (savingNode) {
      savingNode.removeAttribute('aria-busy');
      const savings = Math.max(0, originalPrice - finalPrice);
      this.renderSavingNode(savingNode, offer, savings, currency);
      savingNode.hidden = savings <= 0;
    }
  }

  clearMoneyNode(node) {
    node.replaceChildren();
  }

  renderMoneyNode(node, cents, currency) {
    const normalizedCents = Math.max(0, Math.round(Number(cents || 0)));
    node.textContent = this.formatMoney(normalizedCents, currency);
  }

  renderSavingNode(node, offer, cents, currency) {
    const normalizedCents = Math.max(0, Math.round(Number(cents || 0)));
    node.replaceChildren();

    if (normalizedCents <= 0) return;

    const key = offer === 'one' ? 'offerOneSavingPrefix' : 'offerTwoSavingPrefix';
    const prefix = String(this.dataset[key] || 'Save').trim();
    const formattedMoney = this.formatMoney(normalizedCents, currency);
    node.textContent = prefix ? `${prefix} ${formattedMoney}` : formattedMoney;
  }

  getShopCurrency() {
    return this.normalizeCurrency(this.dataset.shopCurrency) || 'USD';
  }

  getLiquidCurrency() {
    return this.normalizeCurrency(this.dataset.currency) || this.getShopCurrency();
  }

  getDisplayCurrency() {
    const control = document.querySelector('[name="doubly-currencies"]');
    const converter = window.DoublyGlobalCurrency;
    const candidates = [
      control?.value,
      converter?.currentCurrency,
      converter?.selectedCurrency,
      this.dataset.currency,
      this.dataset.shopCurrency,
    ];

    for (const candidate of candidates) {
      const currency = this.normalizeCurrency(candidate);
      if (currency) return currency;
    }

    return 'USD';
  }

  normalizeCurrency(value) {
    const currency = String(value || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : '';
  }

  setupThemePriceSync() {
    const priceRoot = this.getThemePriceRoot();

    if (!priceRoot) {
      this.priceSetupAttempts += 1;
      if (this.priceSetupAttempts >= 20) return;

      if (this.priceSyncTimer) clearTimeout(this.priceSyncTimer);
      this.priceSyncTimer = setTimeout(() => {
        this.priceSyncTimer = null;
        this.setupThemePriceSync();
      }, 150);
      return;
    }

    this.priceSetupAttempts = 0;
    this.priceObserver?.disconnect();
    this.themePriceRoot = priceRoot;
    this.priceObserver = new MutationObserver(() => this.scheduleThemePriceSync());
    this.priceObserver.observe(priceRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.scheduleThemePriceSync();
  }

  getThemePriceRoot() {
    const section = this.closest('.shopify-section');
    const roots = Array.from(section?.querySelectorAll('product-price') || []);

    return (
      roots.find(
        (root) =>
          !this.contains(root) &&
          (!root.dataset.productId || root.dataset.productId === this.dataset.productId)
      ) ||
      roots.find((root) => !this.contains(root)) ||
      null
    );
  }

  getThemeCurrentPriceNode() {
    const priceRoot = this.themePriceRoot?.isConnected
      ? this.themePriceRoot
      : this.getThemePriceRoot();
    const container = priceRoot?.querySelector('[ref="priceContainer"]');
    if (!container) return null;

    return (
      Array.from(container.children).find(
        (node) =>
          node.classList?.contains('price') &&
          !node.classList.contains('compare-at-price')
      ) || null
    );
  }

  scheduleThemePriceSync(delay = 30) {
    if (this.priceSyncTimer) clearTimeout(this.priceSyncTimer);

    this.priceSyncTimer = setTimeout(() => {
      this.priceSyncTimer = null;
      this.syncFromThemePrice();
    }, delay);
  }

  syncFromThemePrice() {
    if (!this.isConnected) return;

    if (!this.themePriceRoot?.isConnected) {
      this.setupThemePriceSync();
      return;
    }

    const currentVariant = this.getCurrentMainVariant();
    const sourceCents = Number(currentVariant?.price || 0);
    const currentPriceNode = this.getThemeCurrentPriceNode();
    const displayedAmount = this.parseMoneyText(currentPriceNode?.textContent || '');

    if (!(sourceCents > 0) || !(displayedAmount >= 0)) return;

    const variantRate = displayedAmount / (sourceCents / 100);
    if (!this.isValidCurrencyRate(variantRate)) return;

    let shopToLiquidRate = 1;
    const liquidCurrency = this.getLiquidCurrency();
    const shopCurrency = this.getShopCurrency();

    if (liquidCurrency !== shopCurrency) {
      const activeCurrency = this.normalizeCurrency(window.Shopify?.currency?.active);
      const marketRate = Number(window.Shopify?.currency?.rate);

      if (
        (!activeCurrency || activeCurrency === liquidCurrency) &&
        this.isValidCurrencyRate(marketRate)
      ) {
        shopToLiquidRate = marketRate;
      }
    }

    this.displayVariantRate = variantRate;
    this.displayShopRate = variantRate * shopToLiquidRate;
    this.updateOfferPrice('one');
    this.updateOfferPrice('two');
  }

  isValidCurrencyRate(rate) {
    return Number.isFinite(rate) && rate > 0.000001 && rate < 1000000;
  }

  parseMoneyText(value) {
    let number = String(value || '').replace(/[^0-9,.-]/g, '');
    if (!number) return NaN;

    const lastComma = number.lastIndexOf(',');
    const lastDot = number.lastIndexOf('.');
    let decimalMark = '';

    if (lastComma >= 0 && lastDot >= 0) {
      decimalMark = lastComma > lastDot ? ',' : '.';
    } else {
      const mark = lastComma >= 0 ? ',' : lastDot >= 0 ? '.' : '';
      if (mark) {
        const lastIndex = number.lastIndexOf(mark);
        const decimalDigits = number.length - lastIndex - 1;
        const occurrences = number.split(mark).length - 1;
        const currency = this.getDisplayCurrency();
        let precision = 2;

        try {
          precision = new Intl.NumberFormat(this.dataset.locale || undefined, {
            style: 'currency',
            currency,
          }).resolvedOptions().maximumFractionDigits;
        } catch {
          // Two decimals is the safe fallback for the store's active currencies.
        }

        if (occurrences === 1 && decimalDigits > 0 && decimalDigits <= precision) {
          decimalMark = mark;
        }
      }
    }

    if (decimalMark) {
      const thousandsMark = decimalMark === ',' ? '.' : ',';
      number = number.replaceAll(thousandsMark, '');
      const decimalIndex = number.lastIndexOf(decimalMark);
      number =
        number.slice(0, decimalIndex).replaceAll(decimalMark, '') +
        '.' +
        number.slice(decimalIndex + 1);
    } else {
      number = number.replace(/[,.]/g, '');
    }

    return Number(number);
  }

  requestMissingSelection() {
    const pool = this.getOptionsPool();
    const selections = this.offerSelections[this.selectedOffer];

    selections.forEach((item, index) => {
      if (!item) {
        selections[index] =
          pool[index % Math.max(pool.length, 1)] ||
          pool[0] ||
          this.getCurrentMainVariant() ||
          null;
      }
    });

    this.renderOffer(this.selectedOffer);
    this.updateOfferPrice(this.selectedOffer);

    return selections.some((item) => !item);
  }

  getCartItems(mainVariantId, quantity = 1) {
    const selections = this.offerSelections[this.selectedOffer].filter(Boolean);
    const multiplier = Math.max(1, Number(quantity) || 1);
    const grouped = new Map();

    for (const item of selections) {
      const id = String(item.variantId);
      grouped.set(id, (grouped.get(id) || 0) + multiplier);
    }

    return Array.from(grouped.entries()).map(([variantId, itemQuantity]) => ({
      variantId,
      quantity: itemQuantity,
    }));
  }

  formatMoney(cents, currency = this.getShopCurrency()) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.normalizeCurrency(currency) || this.getShopCurrency(),
      }).format(Number(cents || 0) / 100);
    } catch {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  }

  startCountdown() {
    const target = this.querySelector('[data-countdown]');
    const minutes = Number(this.dataset.countdownMinutes || 0);

    if (!target || minutes <= 0) return;

    let remaining = minutes * 60;

    const render = () => {
      const mins = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      target.textContent = `${String(mins).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      remaining = remaining > 0 ? remaining - 1 : minutes * 60;
    };

    render();
    this.countdownTimer = setInterval(render, 1000);
  }

  startOfferCountdowns() {
    const timers = Array.from(this.querySelectorAll('[data-offer-countdown]'));

    timers.forEach((timer) => {
      const target = timer.querySelector('[data-offer-countdown-value]');
      const hours = Math.max(0, Number(timer.dataset.hours || 0));
      const minutes = Math.max(0, Number(timer.dataset.minutes || 0));
      const durationSeconds = Math.round(hours * 3600 + minutes * 60);

      this.renderRandomStock(timer);

      if (!target || durationSeconds <= 0) return;

      // A fresh end time is created on every page load. The timer reaches zero,
      // stays there, and starts again from the configured duration after reload.
      const endTime = Date.now() + durationSeconds * 1000;
      let intervalId = null;

      const render = () => {
        const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
        const remainingHours = Math.floor(remaining / 3600);
        const remainingMinutes = Math.floor((remaining % 3600) / 60);
        const remainingSeconds = remaining % 60;

        target.textContent = [remainingHours, remainingMinutes, remainingSeconds]
          .map((value) => String(value).padStart(2, '0'))
          .join(':');

        if (remaining <= 0 && intervalId) {
          clearInterval(intervalId);
          this.offerCountdownTimers = this.offerCountdownTimers.filter(
            (storedTimer) => storedTimer !== intervalId
          );
        }
      };

      render();
      intervalId = setInterval(render, 1000);
      this.offerCountdownTimers.push(intervalId);
    });
  }

  renderRandomStock(timer) {
    const stockTarget = timer.querySelector('[data-offer-random-stock]');
    const showRandomStock = timer.dataset.showRandomStock === 'true';

    if (!stockTarget || !showRandomStock) return;

    const enteredMin = Math.round(Number(timer.dataset.stockMin || 5));
    const enteredMax = Math.round(Number(timer.dataset.stockMax || 100));
    const min = Math.max(5, Math.min(100, enteredMin, enteredMax));
    const max = Math.max(min, Math.min(100, Math.max(enteredMin, enteredMax)));
    const stock = Math.floor(Math.random() * (max - min + 1)) + min;
    const template = String(stockTarget.dataset.stockTemplate || '[stock]');

    if (template.includes('[stock]')) {
      stockTarget.textContent = template.replaceAll('[stock]', String(stock));
    } else if (/\d+/.test(template)) {
      // Replace an existing stock number instead of showing a second number.
      stockTarget.textContent = template.replace(/\d+/, String(stock));
    } else {
      stockTarget.textContent = `${template} ${stock}`.trim();
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

if (!customElements.get('product-bundle-offer')) {
  customElements.define('product-bundle-offer', ProductBundleOffer);
}